/**
 * animations.ts — shared Framer Motion animation presets.
 *
 * Each component type has its own preset. Import the one you need:
 *   import { sidebarItem, cardHover, buttonPress } from './primitives/animations';
 *
 * Usage with motion:
 *   <motion.div {...sidebarItem.hover(hovered, active)} />
 */

import { duration, ease, space } from './tokens';

// ── Sidebar items ───────────────────────────────────────────────────────────

export const sidebarItem = {
  /** Nudge right on hover (inactive items only) */
  hover: (hovered: boolean, active: boolean) => ({
    animate: { x: !active && hovered ? space[1] : 0 },
    transition: { duration: duration.normal / 1000, ease: ease.standard as unknown as [number, number, number, number] },
  }),
};

// ── Cards ────────────────────────────────────────────────────────────────────

export const cardHover = {
  /** Featured card: lift + subtle scale on hover */
  featured: (hovered: boolean) => ({
    animate: { y: hovered ? -space[1] : 0, scale: hovered ? 1.01 : 1 },
    transition: { duration: duration.slow / 1000, ease: ease.standard as unknown as [number, number, number, number] },
  }),
  /** Standard card: no movement */
  standard: {},
};

// ── Buttons ──────────────────────────────────────────────────────────────────

export const buttonPress = {
  whileTap: { scale: 0.98 },
  transition: { duration: duration.normal / 1000 },
};

// ── Floating surfaces (dropdowns, popovers, modals) ─────────────────────────

export const floatingEntrance = {
  dropdown: {
    initial: { opacity: 0, y: space[1] },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: space[1] },
    transition: { duration: duration.normal / 1000 },
  },
  modal: {
    initial: { opacity: 0, scale: 0.97 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.97 },
    transition: { duration: duration.slow / 1000, ease: ease.decelerate as unknown as [number, number, number, number] },
  },
  panel: {
    initial: { x: '100%', opacity: 0 },
    animate: { x: 0, opacity: 1 },
    exit: { x: '100%', opacity: 0 },
    transition: { type: 'tween' as const, duration: duration.emphasis / 1000 },
  },
  toast: {
    initial: { opacity: 0, y: space[5] },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -space[5] },
    transition: { duration: duration.slow / 1000 },
  },
};

// ── Collapse/expand ──────────────────────────────────────────────────────────

export const collapseExpand = {
  initial: { height: 0, opacity: 0 },
  animate: { height: 'auto' as const, opacity: 1 },
  exit: { height: 0, opacity: 0 },
  transition: { duration: duration.slow / 1000 },
};

// ── Chevron rotation ─────────────────────────────────────────────────────────

export const chevronRotate = (expanded: boolean) => ({
  animate: { rotate: expanded ? 90 : 0 },
  transition: { duration: duration.normal / 1000 },
});

// ── Fade in/out ──────────────────────────────────────────────────────────────

export const fade = {
  in: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    transition: { duration: duration.normal / 1000 },
  },
  out: {
    exit: { opacity: 0 },
    transition: { duration: duration.normal / 1000 },
  },
};

// ── Library grid items ──────────────────────────────────────────────────────

export const libraryCard = {
  /** Staggered entrance for grid items — subtle fade + slide */
  entrance: (index: number) => ({
    initial: { opacity: 0, y: space[2] },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0 },
    transition: { duration: duration.emphasis / 1000, ease: ease.decelerate as unknown as [number, number, number, number], delay: index * 0.03 },
  }),
  /** Hover lift for cards */
  hover: {
    whileHover: { y: -space[2], scale: 1.02 },
    transition: { duration: duration.slow / 1000 },
  },
};

export const libraryHeader = {
  title: {
    initial: { opacity: 0, x: -space[5] },
    animate: { opacity: 1, x: 0 },
    transition: { duration: duration.atmospheric / 1000 },
  },
  actions: {
    initial: { opacity: 0, x: space[5] },
    animate: { opacity: 1, x: 0 },
    transition: { duration: duration.atmospheric / 1000 },
  },
  filters: {
    initial: { opacity: 0, y: space[2] },
    animate: { opacity: 1, y: 0 },
    transition: { duration: duration.atmospheric / 1000, delay: duration.slow / 1000 },
  },
};

// ── Tab bar ──────────────────────────────────────────────────────────────────

export const tabItem = {
  initial: { opacity: 0, scale: 0.95, x: -space[2] },
  animate: { opacity: 1, scale: 1, x: 0 },
  exit: { opacity: 0, scale: 0.9, x: -space[2] },
  transition: { duration: duration.slow / 1000 },
};

// Pressable — re-exported for consumers using the primitives barrel
export { Pressable } from './motion';
export type { PressEffect } from './motion';
